import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

// Functional component for error fallback UI
const ErrorFallback: React.FC<{
  onRetry: () => void;
  onReload: () => void;
  onGoHome: () => void;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}> = ({
  onRetry,
  onReload,
  onGoHome,
  error,
  errorInfo
}) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="max-w-md w-full space-y-4">
        <Alert className="border-destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-destructive">
            <div className="font-medium">{t('common.error')}</div>
            <div className="text-sm mt-1">
              {t('common.errorLoadingData')}
            </div>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Button
            onClick={onRetry}
            className="w-full"
            variant="default"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common.retry')}
          </Button>

          <Button
            onClick={onReload}
            className="w-full"
            variant="outline"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>

          <Button
            onClick={onGoHome}
            className="w-full"
            variant="ghost"
          >
            <Home className="mr-2 h-4 w-4" />
            {t('nav.dashboard')}
          </Button>
        </div>

        {/* Show error details in development */}
        {import.meta.env.DEV && error && (
          <details className="mt-4 p-2 bg-muted rounded text-xs">
            <summary className="cursor-pointer font-medium text-destructive">
              Error Details (Development)
            </summary>
            <pre className="mt-2 whitespace-pre-wrap break-words">
              {error.toString()}
              {errorInfo?.componentStack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to console for debugging
    console.error('🚨 Error Boundary caught an error:', error, errorInfo);
    
    this.setState({ 
      hasError: true, 
      error, 
      errorInfo 
    });

    // You could also log to an error reporting service here
    // reportError(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorFallback
          onRetry={this.handleRetry}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

// Hook for components that might throw errors
export function useErrorHandler() {
  return (error: Error, errorInfo?: any) => {
    console.error('Component error:', error, errorInfo);
    // Force a re-render that will be caught by error boundary
    throw error;
  };
}
